import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AddActionButton from 'components/AddActionButton';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithSep
} from 'components/final-form/dropdownUtils';
import Radio from 'components/final-form/Radio';
import { generateRadioButtonOptionsFromEnumAndPrefix } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertStockOrderForm from 'components/medicaments/stockorder/UpsertStockOrderForm';
import PatientInfoView from 'components/patient-entry/bookings-table/PatientInfoView';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Dimmer, Grid, Icon, Loader, Message, Modal, Popup, RadioProps } from 'semantic-ui-react';
import { getAllArticleGroups } from 'services/articleGroupServices';
import { getCompanyArticleGroupsForCompany, searchCompanies } from 'services/companyServices';
import { searchEmployees } from 'services/employeeServices';
import { getPatientForPatientCentral } from 'services/patientServices';
import {
  deleteStockOrder,
  deliverStockOrder,
  findStockOrderById,
  searchStockOrders
} from 'services/stockOrderServices';
import { getAllTestLaboratory } from 'services/testLaboratoryServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { InterventionExternalCode, OrderStatus } from 'ts-types/api.enums';
import {
  ArticleGroupDto,
  CompanyDto,
  CompanySearchRequest,
  DivisionDto,
  EmployeeDto,
  PatientInfoDto,
  StockOrderDto,
  StockOrderSearchRequest,
  TestLaboratoryDto,
  UpsertStockOrderDto
} from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';


const StyledCell = styled.div`
  text-align: right;
  padding-right: 5rem;
`;

interface StockOrderFieldProps extends FormState {
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  referenceDataLoaded: boolean,
  searchValues: Partial<StockOrderSearchRequest>
  foundStockOrders: Array<StockOrderDto>;
  stockOrdersLoaded: boolean;
  stockOrder?: UpsertStockOrderDto;
  stockOrderId?: number;
  articleGroups: Array<ArticleGroupDto>,
  articleGroupOptions: Array<DropdownOption>,
  companies: Array<CompanyDto>,
  companyOptions: Array<DropdownOption>,
  searchCompaniesValues: Partial<CompanySearchRequest>,
  // shippableMaterials: Array<MedicamentDto>,
  // shippableMaterialOptions: Array<DropdownOption>,
  testLaboratories: Array<TestLaboratoryDto>,
  employees: Array<EmployeeDto>,
  employeeOptions: Array<DropdownOption>,
  divisions: Array<DivisionDto>,
  employeeInfo?: PatientInfoDto,
  employeeInfoLoading: boolean,
  addEditModalOpen: boolean;
  loading: boolean;
  cancelTokenSource: CancelTokenSource;
  errorMessage?: String;
}

class StockOrdersView extends Component<Props, State> {

  private mounted: boolean = false;

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();

  private initialSearchValues: Partial<StockOrderSearchRequest> = {
    searchStatus: "ALL",
    // medicamentId: undefined,
    employeeId: undefined,
    articleGroupId: undefined,
    companyId: undefined
  }

  constructor(props: Props) {
    super(props);

    const {radioMap} = this;
    const {getMessage, currentUser} = props;
    const cancelTokenSource = axios.CancelToken.source();

    let orderStatusValues = Object.values(OrderStatus);
    orderStatusValues = ["ALL"].concat(orderStatusValues);

    const radioDefinitions = generateRadioButtonOptionsFromEnumAndPrefix(
        orderStatusValues, "searchStatus", "enum.status", getMessage);

    radioDefinitions.forEach((radioDefinitions, index) => {
      radioMap.set(orderStatusValues[index], radioDefinitions);
    });

    this.state = {
      referenceDataLoaded: false,

      searchValues: {
        searchStatus: "ALL",
        medicamentId: undefined,
        articleGroupId: undefined,
        companyId: undefined,
        employeeId: undefined,
        mandateId: currentUser!.userConfig.mandateId
      },

      foundStockOrders: [],
      stockOrdersLoaded: true,
      stockOrder: undefined,
      stockOrderId: undefined,
      articleGroups: [],
      articleGroupOptions: [],
      // shippableMaterials: [],
      // shippableMaterialOptions: [],
      companies: [],
      companyOptions: [],
      searchCompaniesValues: {},
      testLaboratories: [],
      employees: [],
      employeeOptions: [],
      divisions: [],

      employeeInfoLoading: false,
      addEditModalOpen: false,
      loading: false,
      cancelTokenSource
    };

    this.loadReferenceData(cancelTokenSource);

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  loadReferenceData = async (cancelTokenSource: CancelTokenSource) => {
    try {
      const {searchValues} = this.state;
      const {currentUser} = this.props;
      const {searchCompaniesValues} = this.state;
      searchCompaniesValues.mandateId = currentUser!.userConfig.mandateId;

      const articleGroups = await getAllArticleGroups(cancelTokenSource);
      // const shippableMaterials = await getAllShippableMedicaments(cancelTokenSource);
      const companiesResponse = await searchCompanies(searchCompaniesValues, cancelTokenSource);
      const companies = companiesResponse.filter(c => c.guarantorCompany === false);
      const testLaboratories = await getAllTestLaboratory(cancelTokenSource);
      const employeeSearchRequest = {companyId: searchValues.companyId, mandateId: searchValues.mandateId};
      const employees = await searchEmployees(employeeSearchRequest, cancelTokenSource);

      this.setState({
        articleGroups: articleGroups ? articleGroups : [],
        articleGroupOptions: mapToDropdownOptionArrayWithSep(
            articleGroups, ["description", "externalOrderNumber", "testLaboratoryDescription"], "id"),
        // shippableMaterials: shippableMaterials ? shippableMaterials : [],
        // shippableMaterialOptions: mapToDropdownOptionArray(
        //     shippableMaterials, ["code", "description"], "id", "id"),
        companies: companies ? companies : [],
        companyOptions: mapToDropdownOptionArray(
            companies, ["description", "city"], "id", "id"),
        employees: employees ? employees : [],
        employeeOptions: mapToDropdownOptionArray(
            employees, ["firstName", "name"], "id", "id"),
        testLaboratories: testLaboratories ? testLaboratories : []
      });

    } finally {
      this.setState({
        referenceDataLoaded: true
      });
    }
  }

  openAddEditModal = (id: number | undefined, status: string) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      if (status === OrderStatus.ORDER) {
        findStockOrderById(id, cancelTokenSource)
            .then(response => {
              this.showModal(id, response);
            })
            .catch(noop);
      }
    }
  };

  showModal = (id?: number, stockOrder?: UpsertStockOrderDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        stockOrder: stockOrder,
        stockOrderId: id
      };
    });
  };

  hideModal = () => {
    this.setState(({}: State) => ({
      addEditModalOpen: false,
      stockOrderId: undefined,
      stockOrder: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        stockOrdersLoaded: true
      })
    };

    searchStockOrders(this.state.searchValues, cancelTokenSource)
        .then(response =>
            this.setState({
              foundStockOrders: response
            })
        )
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  }

  handleChange = debounce(({values}: StockOrderFieldProps, form: FormApi): void => {
    if (!this.mounted) {
      return;
    }
    const {articleGroups, cancelTokenSource} = this.state;

    if (values.companyId === undefined || values.articleGroupId === undefined) {
      form.change("employeeId", undefined)
    }

    if (values.companyId === undefined && this.state.searchValues.companyId !== undefined) {

      this.setState({
        articleGroupOptions: mapToDropdownOptionArrayWithSep(
            articleGroups, ["description", "externalOrderNumber", "testLaboratoryDescription"], "id")
      })
    }

    if (values.companyId !== undefined && values.companyId !== this.state.searchValues.companyId) {

      this.onCompanyChange(values.companyId, form);
    }
    this.setState({
      searchValues: values
    });

    const onFinally = () => {
      this.setState({
        stockOrdersLoaded: true
      })
    };

    this.setState({stockOrdersLoaded: false});

    searchStockOrders(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundStockOrders: response
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300)

  deleteStockOrderById = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteStockOrder(id, cancelTokenSource)
        .then(response => {
          this.setState(({foundStockOrders}: State) => ({
            foundStockOrders: foundStockOrders.filter(
                (stockOrder: StockOrderDto) => stockOrder.id != response
            )
          }));
        })
        .catch((error) => this.handleError(error.response.data));
  };

  deliverStockOrderById = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deliverStockOrder(id, cancelTokenSource)
        .then(response => {
          this.setState(({foundStockOrders}: State) => {
            let filteredStockOrders = [...foundStockOrders];
            const stockOrderIx: number = filteredStockOrders.findIndex(st => st.id === response);

            if (stockOrderIx || stockOrderIx >= 0) {
              filteredStockOrders[stockOrderIx].status = OrderStatus.DELIVER;
            }
            return ({
              foundStockOrders: filteredStockOrders
            })
          });
        })
        .catch((error) => this.handleError(error.response.data));
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: string[] = [];

    if (knownErrors.includes(errorCode)) {
      this.setErrorMessage(getMessage(`stockOrder.error.${errorCode}`))
    } else {
      this.setErrorMessage(getMessage('error.general'))
    }
  }

  setErrorMessage = (error?: String) => {
    this.setState({
      errorMessage: error
    })
  }

  onCompanyChange = async (companyId: any, form: FormApi) => {
    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;
    const {articleGroups, cancelTokenSource} = this.state;

    form.change("articleGroupId", undefined);
    form.change("employeeId", undefined);
    ``
    const companyArticleGroups = await getCompanyArticleGroupsForCompany(companyId, cancelTokenSource);
    let filteredArticleGroups: Array<ArticleGroupDto> = [];
    if (companyArticleGroups.length > 0) {
      const articleGroupIds = companyArticleGroups.map(ag => ag.articleGroupId);
      filteredArticleGroups = articleGroups.filter(ag => articleGroupIds.includes(ag.id));
    }
    const articleGroupOptions = mapToDropdownOptionArrayWithSep(
        filteredArticleGroups, ["description", "externalOrderNumber", "testLaboratoryDescription"], "id")

    const searchValues = {companyId: companyId, mandateId: mandateId}
    let companyEmployees = await searchEmployees(searchValues, cancelTokenSource);
    companyEmployees = companyEmployees
        .filter(e => e.testType === InterventionExternalCode.PCR_SALIVA_TEST_INDIVIDUAL_TEST.toString());

    const employeeOptions = mapToDropdownOptionArrayWithSep(
        companyEmployees, ["firstName", "name"], "id", "id")

    this.setState({
      articleGroupOptions: articleGroupOptions,
      employeeOptions: employeeOptions
    });

  }

  onEmployeeNameClick = (patientId: number) => {

    const {cancelTokenSource} = this.state;

    this.setState({
      employeeInfoLoading: true
    });

    getPatientForPatientCentral(patientId, cancelTokenSource)
        .then(response => {
          this.setState({
            employeeInfo: response
          });
        })
        .finally(() => {
          this.setState({
            employeeInfoLoading: false
          });
        });
  };

  clearEmployeeInfoState = () => {
    this.setState({
      employeeInfo: undefined
    })
  }

  render(): React.ReactNode {
    const {stockOrdersLoaded} = this.state;
    return (
        <PraxStyledSegment>

          <div>
            {this.renderSearchStockOrdersSegment()}
          </div>

          <StyledViewTableRow>
            {!stockOrdersLoaded
                ? this.renderLoader()
                : this.renderStockOrdersSegment()}
          </StyledViewTableRow>

          {this.renderUpsertStockOrderModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchStockOrdersSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialSearchValues}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({form, values}: FormRenderProps): React.ReactNode => {
    const {getMessage} = this.props;
    const {
      errorMessage,
      companyOptions,
      articleGroups,
      articleGroupOptions,
      employeeOptions,
      foundStockOrders
    } = this.state;
    const {radioMap} = this;

    const numResults = foundStockOrders.length;

    const statusAll = radioMap.get("ALL");
    const statusOrder = radioMap.get(OrderStatus.ORDER);
    const statusDeliver = radioMap.get(OrderStatus.DELIVER);
    const statusCancel = radioMap.get(OrderStatus.CANCEL);

    let disableEmployeeSelect = true;
    if (values && values.companyId !== undefined && values.articleGroupId !== undefined) {
      const articleGroup = articleGroups.find(ag => ag.id === values.articleGroupId);
      if (articleGroup) {
        disableEmployeeSelect = articleGroup.externalCode !== InterventionExternalCode.PCR_SALIVA_TEST_INDIVIDUAL_TEST;
      }
    }

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("stockOrder.orderStatus")}:
            </Grid.Column>
            <Grid.Column width={1}>
              <Field
                  name="searchStatus"
                  component={Radio}
                  radioDefinition={statusAll}
              />
            </Grid.Column>
            <Grid.Column width={1}>
              <Field
                  name="searchStatus"
                  component={Radio}
                  radioDefinition={statusOrder}
              />
            </Grid.Column>
            <Grid.Column width={1}>

              <Field
                  name="searchStatus"
                  component={Radio}
                  radioDefinition={statusDeliver}
              />
            </Grid.Column>
            <Grid.Column width={1}>
              <Field
                  name="searchStatus"
                  component={Radio}
                  radioDefinition={statusCancel}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("company.title")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="companyId"
                  component={Select}
                  options={companyOptions}
                  clearable
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("stockOrder.articleGroupId")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="articleGroupId"
                  component={Select}
                  options={articleGroupOptions}
                  clearable
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            {/*<Grid.Column width={3}>*/}
            {/*  {getMessage("stockOrders.search.shippable")}:*/}
            {/*</Grid.Column>*/}
            {/*<Grid.Column width={6}>*/}
            {/*  <Field*/}
            {/*      name="medicamentId"*/}
            {/*      component={Select}*/}
            {/*      options={shippableMaterialOptions}*/}
            {/*      clearable*/}
            {/*      fluid*/}
            {/*  />*/}
            {/*</Grid.Column>*/}
            <Grid.Column width={3}>
              {getMessage("stockOrder.employeeId")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="employeeId"
                  component={Select}
                  options={employeeOptions}
                  clearable
                  fluid
                  disabled={disableEmployeeSelect}
              />
            </Grid.Column>
            <Grid.Column width={7} textAlign={'right'}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </PxGrid>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative onDismiss={() => this.setErrorMessage()}>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">
            {getMessage("stockOrder.loading")}
          </Loader>
        </Dimmer>
    );
  };

  renderStockOrdersSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundStockOrders} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundStockOrders.length}
            rowGetter={this.stockOrderRowGetter}
            rowRenderer={this.foundStockOrdersRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 50,
                label: "#",
                dataKey: "index"
              },
              {
                width: 350,
                flexGrow: 1,
                label: getMessage("stockOrder.companyId"),
                dataKey: "companyId",
                cellRenderer: this.companyCellRenderer
              },
              {
                width: 350,
                label: getMessage("stockOrder.divisionId"),
                dataKey: "divisionName"
              },
              {
                width: 150,
                flexGrow: 1,
                label: getMessage("common.patientId"),
                dataKey: "patientId",
              },
              {
                width: 350,
                flexGrow: 1,
                label: getMessage("stockOrder.employeeId"),
                dataKey: "employeeId",
                cellRenderer: this.employeeCelRenderer
              },
              {
                width: 100,
                label: getMessage("stockOrder.quantity"),
                dataKey: "quantity",
                cellRenderer: this.quantityCellRenderer
              },
              {
                width: 400,
                flexGrow: 1,
                label: getMessage("stockOrder.articleGroupId"),
                dataKey: "articleGroupId",
                cellRenderer: this.articleGroupCellRenderer
              },
              // {
              //   width: 350,
              //   label: getMessage("stockOrder.medicamentId"),
              //   dataKey: "medicamentId",
              //   cellRenderer: this.shippableCellRenderer
              // },
              {
                width: 150,
                label: getMessage("stockOrder.orderDate"),
                dataKey: "orderDate",
                cellRenderer: this.dateCellRenderer
              },
              {
                width: 150,
                label: getMessage("stockOrder.deliverDate"),
                dataKey: "deliverDate",
                cellRenderer: this.dateCellRenderer
              },
              {
                width: 200,
                label: getMessage("stockOrder.orderStatus"),
                dataKey: "status",
                cellRenderer: this.stockOrderStatusCellRenderer
              },
              {
                width: 150,
                label: getMessage("actions"),
                dataKey: "id",
                cellRenderer: this.actionsCellRenderer
              }
            ]}
        />
    );
  };

  stockOrderRowGetter = ({index}: any) => {
    const {foundStockOrders} = this.state;

    Object.assign(foundStockOrders[index], {index: index + 1});

    return foundStockOrders[index];
  };

  foundStockOrdersRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};
    const {foundStockOrders} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundStockOrders[index].id, foundStockOrders[index].status)}
        >
          {columns}
        </div>
    );
  };

  quantityCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return (
          <StyledCell>{(cellData)}</StyledCell>
      );
    }
    return (
        <div></div>
    );
  }

  articleGroupCellRenderer = ({cellData}: any) => {
    const {articleGroups} = this.state;

    if (cellData && cellData > 0) {
      const foundAG = articleGroups.filter((ag: ArticleGroupDto) => ag.id === cellData);
      if (foundAG && foundAG.length > 0) {
        let desc = foundAG[0].description;
        if (foundAG[0].externalOrderNumber) {
          desc = desc + ", " + foundAG[0].externalOrderNumber;
        }
        if (foundAG[0].testLaboratoryDescription) {
          desc = desc + ", " + foundAG[0].testLaboratoryDescription;
        }
        return desc;
      }
    }
    return "";
  }

  employeeCelRenderer = ({rowData}: any) => {
    const {getMessage} = this.props;
    const {employeeInfo, employeeInfoLoading} = this.state;

    const loader = (
        <Loader active inline="centered" size="small">{getMessage("loading")}</Loader>
    );

    const employeePopup = (popupTrigger: any) => (
        <Popup
            trigger={popupTrigger}
            popperDependencies={[employeeInfoLoading]}
            popperModifiers={{
              preventOverflow: {
                boundariesElement: 'window'
              }
            }}
            content={
              employeeInfoLoading
                  ? loader
                  : <PatientInfoView
                      showButtons={false}
                      patientInfo={employeeInfo}
                  />
            }
            flowing
            on="click"
            onClose={this.clearEmployeeInfoState}
            size="large"
            position="right center"
        />
    );

    let trigger = rowData.employeeId
        ? (<span>{`${rowData.employeeFirstName} ${rowData.employeeName}`}</span>)
        : "";

    if (rowData.employeeId > 0) {
      return (
          <div title={getMessage("stockOrders.employeeId")} onClick={() => this.onEmployeeNameClick(rowData.patientId)}>
            {employeePopup(trigger)}
          </div>
      );
    }

    return trigger;
  }

  // shippableCellRenderer = ({cellData}: any) => {
  //   const {shippableMaterials} = this.state;
  //
  //   if (cellData && cellData > 0) {
  //     const foundShippable = shippableMaterials.filter((m: MedicamentDto) => m.id === cellData)
  //     if (foundShippable && foundShippable.length > 0) {
  //       let desc = foundShippable[0].description;
  //       if (foundShippable[0].externalOrderNumber) {
  //         desc = desc + ", " + foundShippable[0].externalOrderNumber
  //       }
  //       return desc;
  //     }
  //   }
  //   return "";
  // }

  companyCellRenderer = ({cellData}: any) => {
    const {companies} = this.state;

    if (cellData && cellData > 0) {
      const foundCompany = companies.filter((company: CompanyDto) => company.id === cellData)
      if (foundCompany && foundCompany.length > 0) {
        return foundCompany[0].description;
      }
    }
    return "";
  }

  dateCellRenderer = ({cellData}: any) => {

    if (cellData) {
      return moment(cellData).format("DD.MM.YYYY")
    }
    return "";
  }

  stockOrderStatusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage(`enum.status.${cellData}`)
    }
  }

  actionsCellRenderer = ({cellData, rowData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <AddActionButton
              hoverMessage={getMessage("msgDeliver")}
              onConfirm={() => this.deliverStockOrderById(cellData)}
              disabled={rowData.status === OrderStatus.CANCEL || rowData.status === OrderStatus.DELIVER}
              trigger={<Icon name={'truck'}/>}
          />
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => this.openAddEditModal(cellData, rowData.status)}
              disabled={rowData.status === OrderStatus.CANCEL || rowData.status === OrderStatus.DELIVER}
          />
          <DeleteActionButton
              hoverMessage={getMessage("msgDelete")}
              popupMessage={getMessage("stockOrder.confirmDelete")}
              onConfirm={() => this.deleteStockOrderById(cellData)}
              disabled={rowData.status === OrderStatus.DELIVER}
          />
        </React.Fragment>
    );
  };

  renderUpsertStockOrderModal = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {addEditModalOpen, stockOrder, stockOrderId, articleGroups, companies, searchValues} = this.state;
    const articleGroupId = searchValues.articleGroupId;
    const companyId = searchValues.companyId;
    const employeeId = searchValues.employeeId;
    let stockOrderEffective: Partial<UpsertStockOrderDto> | undefined = stockOrder;
    if (!stockOrderId) {
      stockOrderEffective = {articleGroupId, companyId}
    }

    return (
        <Modal open={addEditModalOpen} closeOnDimmerClick={false} onClose={() => this.hideModal()}>
          <Modal.Header>{getMessage("stockOrder.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertStockOrderForm
                stockOrder={stockOrderEffective}
                stockOrderId={stockOrderId}
                articleGroups={articleGroups} //TODO: OC implement referenceDataLoad and use them here.
                // shippableMaterials={shippableMaterials}
                companies={companies}
                employeeId={employeeId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(withAuthContext(StockOrdersView));

